<script setup lang="ts">
import type { UseFinancingsParams } from '~/store-v2/financings/useFinancings'
import ButtonTag from '../shared/ButtonTag.vue'

export interface QuickFilterType {
  id: string
  label: string
  active: boolean
}

interface Props {
  params: UseFinancingsParams
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:params', params: Partial<UseFinancingsParams>): void
  (e: 'hasActiveFilter', hasActive: boolean): void
}>()

const { t } = useI18n()

const quickFilterConfigs = {
  formalized_90_days: {
    formalized_lte: 90,
    is_expired: false,
    is_canceled: false,
  },
  pending_installations_90_days: {
    formalized_gte: 90,
    is_expired: false,
    is_canceled: false,
    is_installed: false,
  },
  not_installed: {
    formalized_gte: 0,
    is_installed: false,
    is_expired: false,
    is_canceled: false,
  },
}

const activeQuickFilter = computed(() => {
  const { params } = props

  if (params.formalized_gte === 0 && params.is_installed === false && params.is_expired === false && params.is_canceled === false) {
    return 'not_installed'
  }
  if (params.formalized_gte === 90 && params.is_expired === false && params.is_canceled === false && params.is_installed === false) {
    return 'pending_installations_90_days'
  }
  if (params.formalized_lte === 90 && params.is_expired === false && params.is_canceled === false) {
    return 'formalized_90_days'
  }
  return null
})

const quickFilters = computed<QuickFilterType[]>(() => [
  {
    id: 'formalized_90_days',
    label: t('financing.quick_filters.formalized_90_days'),
    active: activeQuickFilter.value === 'formalized_90_days',
  },
  {
    id: 'pending_installations_90_days',
    label: t('financing.quick_filters.pending_installations_90_days'),
    active: activeQuickFilter.value === 'pending_installations_90_days',
  },
  {
    id: 'not_installed',
    label: t('financing.quick_filters.not_installed'),
    active: activeQuickFilter.value === 'not_installed',
  },
])

watch(activeQuickFilter, (newValue) => {
  emit('hasActiveFilter', newValue !== null)
}, { immediate: true })

function handleFilterClick(filterId: string) {
  const isActive = activeQuickFilter.value === filterId

  if (isActive) {
    emit('update:params', {
      page: 1,
      is_installed: undefined,
      formalized_lte: undefined,
      formalized_gte: undefined,
      is_expired: undefined,
      is_canceled: undefined,
    })
  }
  else {
    const config = quickFilterConfigs[filterId as keyof typeof quickFilterConfigs]
    if (config) {
      emit('update:params', {
        page: 1,
        steps: [],
        status: [],
        query: '',
        is_installed: undefined,
        formalized_lte: undefined,
        formalized_gte: undefined,
        ...config,
      })
    }
  }
}
</script>

<template>
  <div class="quick-filters">
    <div class="quick-filters-label">
      {{ t('financing.quick_filters.label') }}
    </div>
    <ButtonTag
      v-for="filter in quickFilters"
      :key="filter.id"
      :label="filter.label"
      :active="filter.active"
      variant="primary"
      size="small"
      @click="handleFilterClick(filter.id)"
    />
  </div>
</template>

<style scoped lang="scss">
.quick-filters {
  @apply flex items-center gap-4 flex-1;
  @apply overflow-x-auto overflow-y-hidden;
  scrollbar-width: thin;
  scrollbar-color: var(--Neutral-High-Medium, #E5E5E5) transparent;

  &::-webkit-scrollbar {
    height: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    @apply bg-neutral-high-medium rounded-2px;
  }

  &::-webkit-scrollbar-thumb:hover {
    @apply bg-neutral-low-light;
  }

  .quick-filters-label {
    @apply text-neutral-low-medium fonts-body-medium-regular whitespace-nowrap flex-shrink-0;
  }

  @screen max-md:system {
    @apply flex-col items-start gap-2 overflow-x-visible overflow-y-visible;

    .quick-filters-label {
      @apply text-xs;
    }
  }
}
</style>
